/**
 * Currency utility functions for working with the Currency class
 */

import Currency from './Currency';
import Cookies from 'js-cookie';

/**
 * Converts a decimal amount to ISO amount (smallest currency unit)
 * @param {number} amount - Decimal amount (e.g., 10.99)
 * @param {string} currencyCode - Three-letter currency code (e.g., 'USD')
 * @returns {number} ISO amount (e.g., 1099 for USD)
 */
export function decimalToIsoAmount(amount, currencyCode) {
  if (typeof amount !== 'number') {
    throw new Error('Amount must be a number');
  }
  
  try {
    const currency = Currency.getUnsafe(currencyCode);
    return currency.toIsoAmount(amount);
  } catch (error) {
    // Default to 2 decimal places if currency not found
    console.warn(`Currency ${currencyCode} not found, using default precision (2)`);
    return Math.round(amount * 100);
  }
}

/**
 * Converts an ISO amount (smallest currency unit) to decimal amount
 * @param {number} isoAmount - ISO amount (e.g., 1099 for USD)
 * @param {string} currencyCode - Three-letter currency code (e.g., 'USD')
 * @param {number} precision - Fallback precision if currency not found
 * @returns {number} Decimal amount (e.g., 10.99)
 */
export function isoToDecimalAmount(isoAmount, currencyCode, precision = 2) {
  if (typeof isoAmount !== 'number') {
    throw new Error('ISO amount must be a number');
  }
  
  try {
    const currency = Currency.getUnsafe(currencyCode);
    return currency.fromIsoAmount(isoAmount);
  } catch (error) {
    // Default to provided precision if currency not found
    console.warn(`Currency ${currencyCode} not found, using provided precision (${precision})`);
    return isoAmount / Math.pow(10, precision);
  }
}

/**
 * Gets the precision (number of decimal places) for a currency
 * @param {string} currencyCode - Three-letter currency code
 * @returns {number} Precision (number of decimal places)
 */
export function getCurrencyPrecision(currencyCode) {
  try {
    const currency = Currency.getUnsafe(currencyCode);
    return currency.exponent;
  } catch (error) {
    // Default to 2 if currency not found
    console.warn(`Currency ${currencyCode} not found, using default precision (2)`);
    return 2;
  }
}

/**
 * Gets a regex pattern for validating input for a specific currency
 * @param {string} currencyCode - Three-letter currency code
 * @returns {RegExp} Regular expression for validating input
 */
export function getCurrencyInputRegex(currencyCode) {
  try {
    const currency = Currency.getUnsafe(currencyCode);
    return currency.getInputRegex();
  } catch (error) {
    // Default to 2 decimal places if currency not found
    console.warn(`Currency ${currencyCode} not found, using default precision (2)`);
    return /^\d*\.?\d{0,2}$/;
  }
}

/**
 * Formats a decimal amount according to currency rules
 * @param {number} amount - Decimal amount to format
 * @param {string} currencyCode - Three-letter currency code
 * @param {Object} options - Formatting options
 * @returns {string} Formatted currency amount
 */
export function formatCurrencyAmount(amount, currencyCode, options = {}) {
  if (amount === null || amount === undefined) {
    return '-';
  }
  
  try {
    const currency = Currency.getUnsafe(currencyCode);
    return currency.format(amount, options);
  } catch (error) {
    // Fall back to basic formatting with default precision
    const { locale = 'en-US', symbol = true } = options;
    const defaultPrecision = 2;
    
    const formatOptions = {
      style: symbol ? 'currency' : 'decimal',
      currency: currencyCode,
      minimumFractionDigits: defaultPrecision,
      maximumFractionDigits: defaultPrecision
    };
    
    return new Intl.NumberFormat(locale, formatOptions).format(amount);
  }
}

/**
 * Gets the default currency from cookies or falls back to EUR
 * @returns {string} Three-letter currency code
 */
export function getDefaultCurrency() {
  return Cookies.get('defaultCurrency') || 'EUR';
}

/**
 * Validates if an amount has the correct number of decimal places for a currency
 * @param {number|string} amount - Amount to validate
 * @param {string} currencyCode - Three-letter currency code
 * @returns {boolean} Whether the amount has valid precision
 */
export function hasValidCurrencyPrecision(amount, currencyCode) {
  try {
    const currency = Currency.getUnsafe(currencyCode);
    return currency.hasValidPrecision(amount);
  } catch (error) {
    // Default to 2 decimal places if currency not found
    console.warn(`Currency ${currencyCode} not found, using default precision (2)`);
    const strAmount = amount.toString();
    const decimalParts = strAmount.split('.');
    
    // If no decimal point, valid for any currency
    if (decimalParts.length === 1) return true;
    
    // Check against default precision (2)
    const decimalDigits = decimalParts[1].length;
    return decimalDigits <= 2;
  }
}